/**
 * Skeleton — the kit's loading placeholder (replaces the bare Mantine re-export).
 *
 * Two deliberate behaviors over a plain box:
 *   - it's FAINT (low-contrast `--skeleton-*` tokens) — a hint of the layout, not a
 *     heavy grey block; and
 *   - it FADES IN after 600ms (see Skeleton.module.css), so a fast load never flashes
 *     a skeleton.
 *
 * Compose several to sketch an abstract version of the real layout (varied widths,
 * thin lines) rather than one big rectangle.
 */
import { forwardRef, type CSSProperties, type HTMLAttributes } from 'react';
import styles from './Skeleton.module.css';

type RadiusToken = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'pill';
const RADIUS_TOKENS = new Set<string>(['xs', 'sm', 'md', 'lg', 'xl', '2xl', 'pill']);

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Height — a number (px, scaled to rem) or any CSS length. */
  readonly height?: number | string;
  /** Width — a number (px → rem) or any CSS length. Defaults to full width. */
  readonly width?: number | string;
  /** Corner radius — a design radius token, a number (px → rem), or a CSS length. */
  readonly radius?: RadiusToken | number | string;
  /** Render a circle (diameter = `height`). */
  readonly circle?: boolean;
}

/** Numbers are treated as px and scaled to rem (accessibility); strings pass through. */
function toLength(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value / 16}rem` : value;
}

function resolveRadius(radius: RadiusToken | number | string): string {
  if (typeof radius === 'number') return `${radius / 16}rem`;
  if (RADIUS_TOKENS.has(radius)) return `var(--radius-${radius})`;
  return radius;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { height, width, radius = 'sm', circle = false, className, style, ...rest },
  ref,
) {
  const size = toLength(height);
  const dims: CSSProperties = {
    height: size,
    width: circle ? size : (toLength(width) ?? '100%'),
    borderRadius: circle ? '50%' : resolveRadius(radius),
  };
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={[styles.root, className].filter(Boolean).join(' ')}
      style={{ ...dims, ...style }}
      {...rest}
    />
  );
});
